// src/pages/LitPoemSection.jsx
//
// Student-facing render of a POEM lit_section (sectionKind 'poem', poetry-anthology).
// Slice PA2. Mirrors the other reader section components (LitThemeSection etc.): pure
// presentational (takes `content`), prose runs through AnnotatedText so glossary terms
// underline, and all colour comes from the book-* theme tokens -> theme-aware in all 3
// reading modes (night / day / muji). objectList blocks render as labelled rows.
//
// Reads POEM_BLOCKS (litSectionShape.js): CORE blocks
//   summary (longText), formStructure (longText), speakerVoiceTone (longText),
//   language ([{device,example,effect}]), themes ([{theme,analysis}]),
//   keyQuotes ([{quote,analysis}])
// then OPTIONAL blocks (rendered only when populated): poet (text, shown as a byline),
// context (longText), comparisonNotes (longText).
//
// Every block is hidden when empty (valueHas guard), so an absent optional block never
// renders an empty section, and a title-only poem shows just the header + an empty note.
import React from 'react';
import AnnotatedText from '../components/AnnotatedText.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';

// Does a value carry real content? (same guard shape as LitSectionPreview.valueHas).
function valueHas(v) {
    if (typeof v === 'string') return v.trim().length > 0;
    if (typeof v === 'number') return Number.isFinite(v);
    if (typeof v === 'boolean') return true;
    if (Array.isArray(v)) return v.some(valueHas);
    if (v && typeof v === 'object') return Object.values(v).some(valueHas);
    return false;
}

function SectionHeading({ children }) {
    return (
        <div className="flex items-center gap-2 mb-3">
            <h3 className="font-display text-book-accent text-sm font-semibold">{children}</h3>
            <div className="flex-grow h-px bg-book-accent/20 ml-2" />
        </div>
    );
}

// A prose (longText) block: heading + AnnotatedText paragraph. Null when empty.
function Prose({ heading, text }) {
    if (!valueHas(text)) return null;
    return (
        <div className="mb-8">
            <SectionHeading>{heading}</SectionHeading>
            <p className="font-serif text-book-text text-base leading-relaxed whitespace-pre-wrap"><AnnotatedText>{text}</AnnotatedText></p>
        </div>
    );
}

export default function LitPoemSection({ content, done = false }) {
    const blocks = (content && content.blocks) || {};
    const summary = blocks.summary;
    const formStructure = blocks.formStructure;
    const speakerVoiceTone = blocks.speakerVoiceTone;
    const language = (Array.isArray(blocks.language) ? blocks.language : []).filter(valueHas);
    const themes = (Array.isArray(blocks.themes) ? blocks.themes : []).filter(valueHas);
    const keyQuotes = (Array.isArray(blocks.keyQuotes) ? blocks.keyQuotes : []).filter(valueHas);
    const poet = blocks.poet;
    const context = blocks.context;
    const comparisonNotes = blocks.comparisonNotes;
    const fullText = blocks.fullText; // public-domain poems only; author-populated, hidden when blank

    const anyContent = [summary, formStructure, speakerVoiceTone, context, comparisonNotes, fullText].some(valueHas)
        || language.length > 0 || themes.length > 0 || keyQuotes.length > 0;

    return (
        <div className="max-w-3xl mx-auto pb-8">
            <LessonHeader eyebrow="Poem" title={(content && content.title) || 'Poem'}
                blocks={blocks} done={done} />
            {valueHas(poet) && <p className="font-serif italic text-book-muted text-base -mt-4 mb-6">{poet}</p>}

            {!anyContent ? (
                <p className="font-serif italic text-book-muted text-sm">Nothing here yet.</p>
            ) : (
                <>
                    {/* Full poem text (public-domain only; author-populated). Rendered first so
                        students read the poem before the analysis. Hidden when blank -- no empty
                        panel. whitespace-pre-wrap preserves the poem's line and stanza breaks;
                        AnnotatedText fires pack-annotation highlights on the verse without re-flow
                        (its matches are inline GlossTerms and it slices text verbatim, keeping \n). */}
                    {valueHas(fullText) && (
                        <div className="mb-8">
                            <SectionHeading>Full text</SectionHeading>
                            <div className="border-l-4 border-book-accent bg-book-pageDark/40 p-5">
                                <p className="font-serif text-book-text text-base leading-relaxed whitespace-pre-wrap"><AnnotatedText>{fullText}</AnnotatedText></p>
                            </div>
                        </div>
                    )}
                    <Prose heading="Summary" text={summary} />
                    <Prose heading="Form & structure" text={formStructure} />
                    <Prose heading="Speaker, voice & tone" text={speakerVoiceTone} />

                    {language.length > 0 && (
                        <div className="mb-8">
                            <SectionHeading>Language &amp; devices</SectionHeading>
                            <div className="space-y-3">
                                {language.map((d, i) => (
                                    <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4">
                                        <div className="flex items-baseline gap-3 flex-wrap mb-1">
                                            {valueHas(d && d.device) && <span className="font-display text-book-accent text-sm font-semibold">{d.device}</span>}
                                            {valueHas(d && d.example) && <span className="font-serif italic text-book-text/80 text-sm">"{d.example}"</span>}
                                        </div>
                                        {valueHas(d && d.effect) && <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap"><AnnotatedText>{d.effect}</AnnotatedText></p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {themes.length > 0 && (
                        <div className="mb-8">
                            <SectionHeading>Themes</SectionHeading>
                            <div className="space-y-3">
                                {themes.map((t, i) => (
                                    <div key={i} className="border-l-4 border-book-accent bg-book-accent/5 p-4">
                                        {valueHas(t && t.theme) && <div className="font-display text-book-accent text-xs mb-2 font-semibold">{t.theme}</div>}
                                        {valueHas(t && t.analysis) && <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap"><AnnotatedText>{t.analysis}</AnnotatedText></p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {keyQuotes.length > 0 && (
                        <div className="mb-8">
                            <SectionHeading>Key quotes</SectionHeading>
                            <div className="space-y-3">
                                {keyQuotes.map((q, i) => (
                                    <div key={i} className="border-l-4 border-book-accent bg-book-pageDark/40 p-5">
                                        {valueHas(q && q.quote) && <p className="font-serif italic text-book-text text-base leading-relaxed mb-2">"{q.quote}"</p>}
                                        {valueHas(q && q.analysis) && <p className="font-serif text-book-text/80 text-sm leading-relaxed">&rarr; <AnnotatedText>{q.analysis}</AnnotatedText></p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <Prose heading="Context" text={context} />
                    <Prose heading="Comparison notes" text={comparisonNotes} />
                </>
            )}
        </div>
    );
}
